import { Button } from '@/components/ui/button';
import type { CartItem } from '@/types/cart';
import { getLocalizedText } from '@/utils/getLocalizedText';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CartItemRowProps {
  item: CartItem;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemove: (itemId: string) => void;
  isUpdating?: boolean;
}

export function CartItemRow({ 
  item, 
  onUpdateQuantity, 
  onRemove,
  isUpdating = false 
}: CartItemRowProps) {
  const { t, i18n } = useTranslation('customer');
  const currentLanguage = (i18n.language === 'ar' ? 'ar' : 'en') as 'ar' | 'en';
  
  // Get localized item name based on current language
  const itemName = getLocalizedText(item, 'name', currentLanguage);
  const unitPriceEGP = (item.unitPrice / 100).toFixed(1);
  const lineTotalEGP = (item.lineTotal / 100).toFixed(1);

  const handleDecrement = () => {
    if (item.quantity > 1) {
      onUpdateQuantity(item.id, item.quantity - 1);
    }
  };

  const handleIncrement = () => {
    if (item.quantity < 99) {
      onUpdateQuantity(item.id, item.quantity + 1);
    }
  };

  return (
    <div 
      className="flex gap-3 py-4 border-b last:border-b-0 animate-in fade-in slide-in-from-right-4 duration-300"
      role="listitem"
      aria-label={`${itemName}, ${item.quantity} ${item.quantity === 1 ? t('cart.item') : t('cart.items')}`}
    >
      {/* Item Image */}
      {item.imageUrl && (
        <div className="flex-shrink-0">
          <img 
            src={item.imageUrl} 
            alt={itemName}
            className="w-16 h-16 object-cover rounded-md"
            loading="lazy"
          />
        </div>
      )}

      {/* Item Details */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm truncate">
              {itemName}
            </h4>
            <p className="text-xs text-muted-foreground mt-1">
              {unitPriceEGP} EGP {t('cart.each')}
            </p>
          </div>

          {/* Line Total */}
          <div className="text-right flex-shrink-0">
            <p className="font-semibold text-sm">
              {lineTotalEGP} EGP
            </p>
          </div>
        </div>

        {/* Quantity Controls + Remove Button */}
        <div className="flex items-center gap-2 mt-3">
          {/* Quantity Controls */}
          <div className="flex items-center gap-1 border rounded-md">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={handleDecrement}
              disabled={item.quantity <= 1 || isUpdating}
              aria-label={t('common:buttons.remove')}
            >
              <Minus className="h-4 w-4" />
            </Button>
            
            <input
              type="number"
              min="1"
              max="99"
              value={item.quantity}
              onChange={(e) => {
                const newQuantity = parseInt(e.target.value, 10)
                if (!isNaN(newQuantity) && newQuantity >= 1 && newQuantity <= 99) {
                  onUpdateQuantity(item.id, newQuantity)
                }
              }}
              className="w-8 text-center text-sm font-medium border-0 bg-transparent focus:outline-none focus:ring-0 p-0"
              aria-label={`Quantity: ${item.quantity}`}
              disabled={isUpdating}
            />
            
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={handleIncrement}
              disabled={item.quantity >= 99 || isUpdating}
              aria-label={t('common:buttons.add')}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Remove Button */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => onRemove(item.id)}
            disabled={isUpdating || item.id.startsWith('temp-')}
            aria-label={`${t('cart.remove')} ${itemName}`}
            title={item.id.startsWith('temp-') ? t('cart.syncing', 'Syncing with server...') : undefined}
          >
            <Trash2 className="h-4 w-4 ltr:mr-1 rtl:ml-1" />
            <span className="text-xs">{t('cart.remove')}</span>
          </Button>
        </div>

        {/* Special Instructions */}
        {item.specialInstructions && (
          <p className="text-xs text-muted-foreground italic mt-2">
            {item.specialInstructions}
          </p>
        )}
      </div>
    </div>
  );
}
